import { motion } from 'framer-motion'
import { EASE_CALM, cn } from '@workflow/ui'
import { formatHours, hoursFigure } from '@/features/jobs/jobsStatus'
import { monthKey } from '@/features/jobs/jobPeriods'

/*
 * The pieces every overview is built from — the player's month on their
 * Home and the studio's on the main page: soft rounded cards, the month's
 * weeks as a row of bars with their hours on them, and the change from the
 * month before said in words.
 */

export interface MonthWeek {
  /** "1–7", "29–31". */
  label: string
  hours: number
  tasks: number
}

export const previousMonth = (key: string): string => {
  const [year, month] = key.split('-').map(Number) as [number, number]
  return monthKey(new Date(year, month - 2, 1))
}

/** A month's days in sevens — "1–7" … "29–31" — each holding the hours and entries that fell on it. */
export function monthWeeks(key: string, entries: readonly { day: number; hours: number }[]): MonthWeek[] {
  const [year, month] = key.split('-').map(Number) as [number, number]
  const days = new Date(year, month, 0).getDate()
  const weeks: MonthWeek[] = []
  for (let start = 1; start <= days; start += 7) {
    const end = Math.min(start + 6, days)
    const inWeek = entries.filter((entry) => entry.day >= start && entry.day <= end)
    weeks.push({
      label: start === end ? String(start) : `${start}–${end}`,
      hours: inWeek.reduce((total, entry) => total + entry.hours, 0),
      tasks: inWeek.length,
    })
  }
  return weeks
}

/** The month against the one before, in words — "2 Hours More Than October" — never a bare sign. */
export function changeText(now: number, before: number, monthBefore: string, empty = 'No Finished Work Yet'): string {
  const change = now - before
  if (now === 0 && before === 0) return empty
  if (change === 0) return `Same As ${monthBefore}`
  const size = Math.abs(change)
  return `${hoursFigure(size)} ${size === 1 ? 'Hour' : 'Hours'} ${change > 0 ? 'More' : 'Less'} Than ${monthBefore}`
}

export function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex flex-col rounded-[28px] bg-tint p-5', className)}>{children}</div>
}

/** The month's weeks as thin rounded bars rising from one baseline, each with its hours written above it. */
export function WeekBars({ weeks }: { weeks: MonthWeek[] }) {
  const top = Math.max(...weeks.map((week) => week.hours), 1)
  return (
    <div className="mt-auto pt-6" role="img" aria-label={weeks.map((week) => `Days ${week.label}: ${formatHours(week.hours)} hours`).join('; ')}>
      <div className="flex h-28 items-end gap-2">
        {weeks.map((week) => (
          <div key={week.label} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
            <span className={cn('font-mono text-micro tabular-nums', week.hours > 0 ? 'text-foreground' : 'text-transparent')}>
              {formatHours(week.hours)}
            </span>
            <motion.div
              className={cn('w-full max-w-10 rounded-t-[4px] rounded-b-[2px]', week.hours > 0 ? 'bg-foreground' : 'bg-foreground/10')}
              initial={false}
              animate={{ height: week.hours > 0 ? `${Math.max(6, (week.hours / top) * 80)}%` : '3px' }}
              transition={{ duration: 0.6, ease: EASE_CALM }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2 border-t border-hairline pt-2">
        {weeks.map((week) => (
          <span key={week.label} className="flex-1 text-center text-micro tabular-nums text-muted-foreground">{week.label}</span>
        ))}
      </div>
    </div>
  )
}
